import { useEffect, useState, useCallback } from "react";
import { useNavigate } from 'react-router-dom';
import { createPortal } from "react-dom";
import { useTranslation } from "../i18n";
import { listEventPhotos } from "../lib/galleryService";
import "../styles/PhotoGallery.css";

const PhotoGallery = ({ eventId, eventName, onBack, onClose }) => {
  const { t } = useTranslation();
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);


  useEffect(() => {
    const fetchPhotos = async () => {
      try {
        setLoading(true);
        const photos = await listEventPhotos(eventId);
        setImages(photos.map((p) => p.url));
      } catch (err) {
        console.error("[PhotoGallery] fetch error:", err);
        setError(t("photos.error"));
      } finally {
        setLoading(false);
      }
    };

    fetchPhotos();

  }, [eventId, t]);

  const nextImage = useCallback(() => {
    setSelectedIndex((prev) => (prev + 1) % images.length);
  }, [images.length]);

  const prevImage = useCallback(() => {
    setSelectedIndex((prev) => (prev - 1 + images.length) % images.length);
  }, [images.length]);

  const openImage = (index) => setSelectedIndex(index);

  const closeImage = () => {
    setSelectedIndex(null);
    document.body.classList.remove("lightbox-open");
  };

  const navigate = useNavigate();

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === "Escape") {
        if (selectedIndex !== null) {
          closeImage();
        } else if (onBack) {
          onBack();
        } else if (onClose) {
          onClose();
          navigate('/');
        }
      }
      if (e.key === "ArrowRight" && selectedIndex !== null) nextImage();
      if (e.key === "ArrowLeft" && selectedIndex !== null) prevImage();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [selectedIndex, onBack, onClose, nextImage, prevImage, navigate]);

  useEffect(() => {
    if (selectedIndex !== null) document.body.classList.add("lightbox-open");
    return () => document.body.classList.remove("lightbox-open");
  }, [selectedIndex]);

  const handleBackClick = () => {
    if (onBack) return onBack();
    if (onClose) {
      onClose();
      navigate('/');
    }
  };

  if (loading) {
    return (
      <div className="photo-gallery-in-screen">
        <div className="photo-gallery-container">
          <button className="gallery-close-btn" onClick={handleBackClick}>
            {onBack ? "←" : "✕"}
          </button>
          <div className="gallery-status">{t("photos.loading")}</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="photo-gallery-in-screen">
        <div className="photo-gallery-container">
          <button className="gallery-close-btn" onClick={handleBackClick}>
            {onBack ? "←" : "✕"}
          </button>
          <div className="gallery-status gallery-error">
            <div>{error}</div>
          </div>
        </div>
      </div>
    );
  }

  if (images.length === 0) {
    return (
      <div className="photo-gallery-in-screen">
        <div className="photo-gallery-container">
          <button className="gallery-close-btn" onClick={handleBackClick}>
            {onBack ? "←" : "✕"}
          </button>
          <div className="gallery-status">
            {eventName
              ? t("photos.emptyNamed", { name: eventName })
              : t("photos.empty")}
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="photo-gallery-in-screen">
        <div className="photo-gallery-container">
          <div className="photo-gallery-header">
            {eventName && <h2 className="photo-gallery-title">{eventName}</h2>}
            <button className="gallery-close-btn" onClick={handleBackClick}>
              {onBack ? "←" : "✕"}
            </button>
          </div>

          <div className="gallery-grid">
            {images.map((img, index) => (
              <button
                key={index}
                className="gallery-grid-item"
                onClick={() => openImage(index)}
                aria-label={t("photos.openAria", { index: index + 1 })}
              >
                <img
                  src={img}
                  alt={t("photos.alt", { index: index + 1 })}
                  loading="lazy"
                  onError={(e) => {
                    e.target.style.border = "2px solid red";
                  }}
                />
              </button>
            ))}
          </div>
        </div>
      </div>

      {selectedIndex !== null &&
        createPortal(
          <div className="gallery-lightbox" onClick={closeImage}>
            <div
              className="gallery-lightbox-content"
              onClick={(e) => e.stopPropagation()}
            >
              <button className="gallery-lightbox-close" onClick={closeImage}>
                ✕
              </button>
              <button
                className="gallery-lightbox-nav prev"
                onClick={(e) => {
                  e.stopPropagation();
                  prevImage();
                }}
              >
                ‹
              </button>
              <img
                src={images[selectedIndex]}
                alt={t("photos.alt", { index: selectedIndex + 1 })}
                className="gallery-lightbox-image"
              />
              <button
                className="gallery-lightbox-nav next"
                onClick={(e) => {
                  e.stopPropagation();
                  nextImage();
                }}
              >
                ›
              </button>
            </div>
          </div>,
          document.body
        )}
    </>
  );
};

export default PhotoGallery;
